import React, { useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Mail, MessageSquare, StickyNote, Loader2, Send } from 'lucide-react';
import MacroPicker from '@/components/admin/crm/MacroPicker';
import MacroDrawer from '@/components/admin/crm/MacroDrawer';
import { insertMacroInto, restoreCaret } from '@/lib/crmMacros';
import { inputClass } from './caseConstants';

const TABS = [
  { id: 'email', label: 'EMAIL REPLY', icon: Mail },
  { id: 'sms', label: 'SMS REPLY', icon: MessageSquare },
  { id: 'note', label: 'INTERNAL NOTE', icon: StickyNote },
];

// Sticky omnichannel composer. Email and SMS both go out through the existing
// delivery functions and hand the case back to the customer; an internal note
// writes a staff note without touching the customer-facing status.
export default function CaseComposer({ caseData, bookingRef, onDone }) {
  const [tab, setTab] = useState('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const bodyRef = useRef(null);

  const switchTab = (next) => {
    setTab(next);
    setError('');
  };

  // Responses land at the caret so a greeting, or a second snippet stacked onto
  // the first, is never lost.
  const insertMacro = (text, macro) => {
    const { next, caret } = insertMacroInto(bodyRef.current, text);
    setBody(next);
    restoreCaret(bodyRef.current, caret);
    if (tab === 'email' && macro.subject && !subject.trim()) setSubject(macro.subject);
  };

  const send = async () => {
    if (!body.trim()) {
      setError('Write a message before sending.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res =
        tab === 'note'
          ? await base44.functions.invoke('caseAddActivity', {
              caseId: caseData.id,
              note: body,
              kind: 'staff_note',
            })
          : await base44.functions.invoke('caseReply', {
              caseId: caseData.id,
              channel: tab,
              subject,
              body,
            });
      if (res.data?.error) throw new Error(res.data.error);
      setBody('');
      setSubject('');
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not send.');
    }
    setSending(false);
  };

  const noAddress = tab === 'email' ? !caseData.customer_email : tab === 'sms' ? !caseData.customer_phone : false;

  return (
    <div className="sticky bottom-0 border border-white/10 bg-[#0d0d0f] shadow-[0_-8px_24px_rgba(0,0,0,0.5)]">
      <div className="flex items-center gap-1 border-b border-white/5 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-[10px] font-bold tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-white/40 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="p-4 space-y-3">
        {tab !== 'note' && (
          <MacroPicker
            channel={tab}
            context={{
              customer_name: caseData.customer_name,
              case_code: caseData.reference_code,
              booking_ref: bookingRef,
            }}
            onInsert={insertMacro}
            onManage={() => setManageOpen(true)}
          />
        )}

        {tab === 'email' && (
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={caseData.subject || 'Subject'}
            className={inputClass}
          />
        )}

        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={tab === 'note' ? 4 : 6}
          placeholder={
            tab === 'email'
              ? `Reply to ${caseData.customer_email || 'the customer'}…`
              : tab === 'sms'
                ? `Text ${caseData.customer_phone || 'the customer'}…`
                : 'Note for the team — the customer will not see this.'
          }
          className={`${inputClass} resize-none leading-relaxed`}
        />

        {noAddress && (
          <p className="text-amber-400/80 text-[11px]">
            This case has no {tab === 'email' ? 'email address' : 'phone number'} on record — add one
            on the customer card first.
          </p>
        )}

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <div className="flex items-center justify-between gap-3">
          <p className="text-white/25 text-[10px]">
            {tab === 'note'
              ? 'Stays internal — the case status is unchanged.'
              : 'Sending hands the case back to the customer.'}
          </p>
          <button
            onClick={send}
            disabled={sending || noAddress}
            className={`flex items-center gap-2 font-bold text-[10px] tracking-widest px-5 py-2.5 transition-colors disabled:opacity-40 ${
              tab === 'note'
                ? 'bg-amber-400 hover:bg-amber-300 text-black'
                : 'bg-primary hover:bg-primary/90 text-white'
            }`}
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? 'SENDING…' : tab === 'note' ? 'ADD NOTE' : 'SEND'}
          </button>
        </div>
      </div>

      <MacroDrawer open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  );
}